import type { KeyboardEvent } from 'react';
import type { SortDir } from '../hooks/useClientSort';
import { useColWidth } from '../hooks/useColWidth';

export interface SortableThProps<K extends string = string> {
  /** Stable column key passed to useClientSort.toggle + width storage. */
  column: K;
  label: string;
  sortKey: K | null;
  sortDir: SortDir;
  onSort: (key: K) => void;
  className?: string;
  /** Right-align (amounts, counts, mileage). */
  numeric?: boolean;
  /** Optional override; default resolves from nearest `[data-table-id]`. */
  tableId?: string;
  /** Disable drag-to-resize on this column (default: resizable). */
  resizable?: boolean;
}

/**
 * Clickable + resizable table header for client-side sort.
 * Pair with `useClientSort` and wrap the table in `<TableScroll id="…">`.
 *
 * Drag the right edge to resize; double-click the edge to reset width.
 * Widths persist in localStorage per table id + column key.
 */
export function SortableTh<K extends string = string>({
  column,
  label,
  sortKey,
  sortDir,
  onSort,
  className,
  numeric,
  tableId,
  resizable = true,
}: SortableThProps<K>): JSX.Element {
  const active = sortKey === column;
  const ariaSort = active
    ? sortDir === 'asc'
      ? 'ascending'
      : 'descending'
    : 'none';

  const { thRef, width, onResizePointerDown, onResizeDoubleClick } = useColWidth(
    String(column),
    tableId,
    resizable,
  );

  const onKeyDown = (e: KeyboardEvent<HTMLTableCellElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSort(column);
    }
  };

  return (
    <th
      ref={thRef}
      scope="col"
      data-col={String(column)}
      className={[
        'sortable-th',
        'resizable-th',
        numeric ? 'sortable-th-numeric' : '',
        active ? 'is-sorted' : '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={width != null ? { width, minWidth: width, maxWidth: width } : undefined}
      aria-sort={ariaSort}
      tabIndex={0}
      title={`Sort by ${label}. Drag edge to resize.`}
      onClick={() => onSort(column)}
      onKeyDown={onKeyDown}
    >
      <span className="sortable-th-inner">
        <span className="sortable-th-label">{label}</span>
        <span className="sortable-th-icon" aria-hidden="true">
          {active ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </span>
      {resizable && (
        <span
          className="col-resize-handle"
          role="separator"
          aria-orientation="vertical"
          aria-label={`Resize ${label} column`}
          title="Drag to resize · double-click to reset"
          onPointerDown={onResizePointerDown}
          onDoubleClick={onResizeDoubleClick}
          onClick={(e) => e.stopPropagation()}
        />
      )}
    </th>
  );
}

/**
 * Non-sortable header that is still resizable (e.g. action columns with a label).
 * For empty action columns, prefer a plain `<th />` without a handle.
 */
export function ResizableTh({
  column,
  label,
  className,
  numeric,
  tableId,
}: {
  column: string;
  label: string;
  className?: string;
  numeric?: boolean;
  tableId?: string;
}): JSX.Element {
  const { thRef, width, onResizePointerDown, onResizeDoubleClick } = useColWidth(column, tableId);

  return (
    <th
      ref={thRef}
      scope="col"
      data-col={column}
      className={['resizable-th', numeric ? 'sortable-th-numeric' : '', className ?? '']
        .filter(Boolean)
        .join(' ')}
      style={width != null ? { width, minWidth: width, maxWidth: width } : undefined}
      title={`${label}. Drag edge to resize.`}
    >
      <span className="sortable-th-inner">
        <span className="sortable-th-label">{label}</span>
      </span>
      <span
        className="col-resize-handle"
        role="separator"
        aria-orientation="vertical"
        aria-label={`Resize ${label} column`}
        title="Drag to resize · double-click to reset"
        onPointerDown={onResizePointerDown}
        onDoubleClick={onResizeDoubleClick}
      />
    </th>
  );
}
